<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelinesAPI from 'dashboard/api/pipelines';
import MessageAPI from 'dashboard/api/inbox/message';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { emitter } from 'shared/helpers/mitt';
import { normalizeLocale } from '../utils/localeFormatting';
import { REPORTING_DEFAULT_TIMEZONE } from 'dashboard/helper/reportingTimezone';
import {
  buildMessageIdempotencyKey,
  createMessageBatchId,
  fingerprintMessageContent,
} from '../helpers/messageIdempotency';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  currentStage: {
    type: [Number, String],
    required: true,
  },
  pipelineId: {
    type: [Number, String],
    default: null,
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
  conversation: {
    type: Object,
    default: () => ({}),
  },
  item: {
    type: Object,
    required: true,
  },
  items: {
    type: Array,
    default: () => [],
  },
  isBulk: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'send', 'sent']);
const { locale, t } = useI18n();
const store = useStore();

const loading = ref(false);
const sendingMessage = ref(false);
const pipelines = ref([]);
const hasLoadError = ref(false);
const selectedTemplate = ref(null);
const showCustomMessage = ref(false);
const customMessage = ref('');
const messageError = ref('');
const isMounted = ref(false);
const activeRequest = ref(0);
const sentTargetKeys = ref(new Set());
const activeBatchFingerprint = ref('');
const activeBatchId = ref('');
const stopRequested = ref(false);
const closeAfterSend = ref(false);

const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const currentAccountId = computed(
  () => store.getters.getCurrentAccountId ?? currentAccount.value.id ?? null
);
const normalizedAccountId = () => {
  const accountId = currentAccountId.value;
  if (accountId === undefined || accountId === null) return null;
  return String(accountId).trim() || null;
};
let accountGeneration = 0;
let sendAbortController = null;
const captureAccountContext = () => ({
  accountId: normalizedAccountId(),
  generation: accountGeneration,
});
const accountContextIsCurrent = context =>
  context.accountId !== null &&
  context.accountId === normalizedAccountId() &&
  context.generation === accountGeneration;
const reportingTimezone = computed(
  () =>
    currentAccount.value.reporting_timezone ||
    currentAccount.value.settings?.reporting_timezone ||
    REPORTING_DEFAULT_TIMEZONE
);
const activeLocale = computed(() => normalizeLocale(locale.value));

const channelName = computed(() => {
  const channelMap = {
    'Channel::WebWidget': t('PIPELINE.CHANNELS.WEBSITE'),
    'Channel::Api': 'API',
    'Channel::Email': t('PIPELINE.CHANNELS.EMAIL'),
    'Channel::TwitterProfile': t('PIPELINE.CHANNELS.TWITTER'),
    'Channel::FacebookPage': t('PIPELINE.CHANNELS.FACEBOOK'),
    'Channel::TelegramBot': t('PIPELINE.CHANNELS.TELEGRAM'),
    'Channel::WhatsApp': t('PIPELINE.CHANNELS.WHATSAPP'),
  };
  return (
    channelMap[props.conversation?.meta?.channel] || t('PIPELINE.CHANNELS.CHAT')
  );
});

const conversationStatus = computed(() => {
  switch (props.conversation?.status) {
    case 'open':
      return t('PIPELINE.SEND_MESSAGE_TEMPLATE.STATUS_OPEN');
    case 'pending':
      return t('PIPELINE.SEND_MESSAGE_TEMPLATE.STATUS_PENDING');
    case 'resolved':
      return t('PIPELINE.SEND_MESSAGE_TEMPLATE.STATUS_RESOLVED');
    case 'snoozed':
      return t('PIPELINE.SEND_MESSAGE_TEMPLATE.STATUS_SNOOZED');
    default:
      return t('PIPELINE.SEND_MESSAGE_TEMPLATE.STATUS_UNKNOWN');
  }
});

const formatDate = value => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(activeLocale.value, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: reportingTimezone.value,
    }).format(new Date(value));
  } catch {
    return '';
  }
};

const getByPath = (source, path) =>
  String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((value, key) => value?.[key], source);

const comparableValue = value => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return value.name || value.title || '';
  return String(value);
};

const matchesRule = (item, rule) => {
  const actual = comparableValue(getByPath(item, rule.field));
  const expected = String(rule.value ?? '');
  switch (rule.operator) {
    case 'contains':
      return actual.toLocaleLowerCase().includes(expected.toLocaleLowerCase());
    case 'starts_with':
      return actual
        .toLocaleLowerCase()
        .startsWith(expected.toLocaleLowerCase());
    case 'ends_with':
      return actual.toLocaleLowerCase().endsWith(expected.toLocaleLowerCase());
    case 'equals':
    default:
      return actual.toLocaleLowerCase() === expected.toLocaleLowerCase();
  }
};

const templateMatchesItem = (template, item) => {
  if (!template.conditions?.enabled) return true;
  const rules = template.conditions.rules || [];
  return rules.length > 0 && rules.every(rule => matchesRule(item, rule));
};

const getConversationId = item =>
  item?.item_details?.conversation?.display_id ||
  item?.conversation_display_id ||
  item?.item_details?.conversation_id ||
  item?.conversation_id;

const targets = computed(() => {
  if (!props.isBulk) {
    return [
      {
        key: 'single:' + String(props.conversationId),
        conversationId: props.conversationId,
        item: props.item,
      },
    ];
  }

  return props.items
    .map((item, index) => {
      const conversationId = getConversationId(item);
      return {
        key:
          String(conversationId || 'missing') + ':' + String(item.id ?? index),
        conversationId,
        item,
      };
    })
    .filter(target => target.conversationId);
});

const activePipelineId = computed(
  () =>
    props.pipelineId ??
    props.item?.pipeline_id ??
    props.item?.funnel_id ??
    props.items[0]?.pipeline_id ??
    props.items[0]?.funnel_id ??
    null
);

const currentStageTemplates = computed(() => {
  const stageId = String(props.currentStage);
  const pipelineId = activePipelineId.value;
  if (pipelineId === null || pipelineId === undefined) return [];

  const candidates = [];

  pipelines.value.forEach(pipeline => {
    if (String(pipeline.id) !== String(pipelineId)) return;

    Object.entries(pipeline.stages || {}).forEach(([id, stage]) => {
      if (String(id) !== stageId) return;
      const templates = Array.isArray(stage.message_templates)
        ? stage.message_templates
        : [];
      templates
        .filter(template => template && typeof template === 'object')
        .forEach((template, index) => {
          const appliesToTargets = targets.value.every(target =>
            templateMatchesItem(template, target.item)
          );
          if (appliesToTargets) {
            candidates.push({
              ...template,
              key:
                String(pipeline.id) +
                ':' +
                stageId +
                ':' +
                String(template.id ?? index),
            });
          }
        });
    });
  });

  return candidates;
});

const fetchPipelines = async () => {
  if (loading.value) return;

  const accountId = normalizedAccountId();
  const requestId = activeRequest.value + 1;
  activeRequest.value = requestId;
  const requestIsCurrent = () =>
    isMounted.value &&
    requestId === activeRequest.value &&
    accountId === normalizedAccountId();
  if (accountId === null) {
    hasLoadError.value = true;
    pipelines.value = [];
    return;
  }

  loading.value = true;
  hasLoadError.value = false;
  try {
    const response = await PipelinesAPI.get(accountId);
    if (!requestIsCurrent()) return;
    pipelines.value = Array.isArray(response.data) ? response.data : [];
    if (
      selectedTemplate.value &&
      !currentStageTemplates.value.some(
        template => template.key === selectedTemplate.value.key
      )
    ) {
      selectedTemplate.value = null;
    }
  } catch {
    if (!requestIsCurrent()) return;
    hasLoadError.value = true;
  } finally {
    if (requestIsCurrent()) {
      loading.value = false;
    }
  }
};

const variableContext = item => ({
  ...item,
  item_details: item?.item_details || {},
  contact: props.contact,
  conversation: props.conversation,
});

const renderTemplate = (content, item) =>
  String(content || '').replace(/\{([^}]+)\}/g, (match, path) => {
    const value = comparableValue(
      getByPath(variableContext(item), path.trim())
    );
    return value || match;
  });

const selectedPreview = computed(() => {
  if (!selectedTemplate.value) return '';
  return renderTemplate(selectedTemplate.value.content, targets.value[0]?.item);
});

const unresolvedVariables = computed(() => {
  if (showCustomMessage.value || !selectedTemplate.value) return [];
  const variables = new Set();
  targets.value.forEach(target => {
    const rendered = renderTemplate(
      selectedTemplate.value.content,
      target.item
    );
    const matches = rendered.match(/\{([^}]+)\}/g) || [];
    matches.forEach(match => variables.add(match));
  });
  return [...variables];
});

const messageContent = computed(() =>
  showCustomMessage.value ? customMessage.value.trim() : selectedPreview.value
);

const messageForTarget = target =>
  showCustomMessage.value
    ? customMessage.value.trim()
    : renderTemplate(selectedTemplate.value?.content, target.item);

const deliveryFingerprint = computed(() =>
  fingerprintMessageContent(
    JSON.stringify({
      mode: showCustomMessage.value ? 'custom' : 'template',
      template: selectedTemplate.value?.key || '',
      targets: targets.value
        .map(target => ({
          key: target.key,
          conversationId: String(target.conversationId),
          content: messageForTarget(target),
        }))
        .sort((left, right) => left.key.localeCompare(right.key)),
    })
  )
);

const sentKeyForTarget = target => `${deliveryFingerprint.value}:${target.key}`;
const pendingTargets = computed(() =>
  targets.value.filter(
    target => !sentTargetKeys.value.has(sentKeyForTarget(target))
  )
);

watch(deliveryFingerprint, (fingerprint, previousFingerprint) => {
  if (fingerprint === previousFingerprint) return;
  sentTargetKeys.value = new Set();
  activeBatchFingerprint.value = '';
  activeBatchId.value = '';
});

const ensureDeliveryBatch = fingerprint => {
  if (activeBatchFingerprint.value !== fingerprint) {
    activeBatchFingerprint.value = fingerprint;
    activeBatchId.value = createMessageBatchId();
  }
  return activeBatchId.value;
};

const canSend = computed(
  () =>
    !loading.value &&
    !sendingMessage.value &&
    pendingTargets.value.length > 0 &&
    Boolean(messageContent.value.trim()) &&
    unresolvedVariables.value.length === 0
);

const selectTemplate = template => {
  selectedTemplate.value = template;
  messageError.value = '';
};

const toggleMessageMode = () => {
  showCustomMessage.value = !showCustomMessage.value;
  messageError.value = '';
};

const handleSendMessage = async () => {
  if (sendingMessage.value || !canSend.value) {
    if (unresolvedVariables.value.length) {
      messageError.value = t(
        'PIPELINE.SEND_MESSAGE_TEMPLATE.UNRESOLVED_VARIABLES',
        { variables: unresolvedVariables.value.join(', ') }
      );
    }
    return;
  }
  const accountContext = captureAccountContext();
  if (accountContext.accountId === null) {
    messageError.value = t('PIPELINE.SEND_MESSAGE_TEMPLATE.SEND_ERROR');
    return;
  }
  const abortController = new AbortController();
  sendAbortController?.abort();
  sendAbortController = abortController;
  const requestIsCurrent = () =>
    isMounted.value &&
    accountContextIsCurrent(accountContext) &&
    sendAbortController === abortController;

  const fingerprint = deliveryFingerprint.value;
  const deliveryBatchId = ensureDeliveryBatch(fingerprint);
  const targetsToSend = pendingTargets.value.map(target => ({
    ...target,
    content: messageForTarget(target),
    sentKey: `${fingerprint}:${target.key}`,
  }));
  sendingMessage.value = true;
  stopRequested.value = false;
  closeAfterSend.value = false;
  messageError.value = '';

  try {
    let failures = 0;
    /* eslint-disable no-restricted-syntax, no-await-in-loop */
    for (const target of targetsToSend) {
      if (stopRequested.value || !requestIsCurrent()) break;

      try {
        await MessageAPI.create(
          {
            conversationId: target.conversationId,
            message: target.content,
            private: false,
          },
          {
            headers: {
              'Idempotency-Key': buildMessageIdempotencyKey({
                scope: 'pipeline-template',
                batchId: deliveryBatchId,
                conversationId: target.conversationId,
                content: target.content,
              }),
            },
            signal: abortController.signal,
            accountId: accountContext.accountId,
          }
        );
        if (!requestIsCurrent()) break;
        if (deliveryFingerprint.value === fingerprint) {
          sentTargetKeys.value.add(target.sentKey);
        }
      } catch {
        if (!requestIsCurrent() || abortController.signal.aborted) break;
        failures += 1;
      }
    }
    /* eslint-enable no-restricted-syntax, no-await-in-loop */

    if (!requestIsCurrent() || stopRequested.value) return;

    if (failures > 0) {
      messageError.value = t('PIPELINE.SEND_MESSAGE_TEMPLATE.PARTIAL_FAILURE', {
        sent: sentTargetKeys.value.size,
        failed: failures,
      });
      emitter.emit('newToastMessage', {
        message: messageError.value,
        action: { type: 'error' },
      });
      return;
    }

    if (pendingTargets.value.length === 0) {
      emitter.emit('newToastMessage', {
        message: props.isBulk
          ? t('PIPELINE.MESSAGES.MESSAGES_SENT_SUCCESS')
          : t('PIPELINE.MESSAGES.MESSAGE_SENT_SUCCESS'),
        action: { type: 'success' },
      });
      emit('send');
      emit('sent');
    }
  } catch {
    if (!requestIsCurrent() || abortController.signal.aborted) return;
    messageError.value = t('PIPELINE.SEND_MESSAGE_TEMPLATE.SEND_ERROR');
    emitter.emit('newToastMessage', {
      message: messageError.value,
      action: { type: 'error' },
    });
  } finally {
    if (sendAbortController === abortController) {
      sendAbortController = null;
    }
    if (accountContextIsCurrent(accountContext)) {
      sendingMessage.value = false;
      if (closeAfterSend.value && isMounted.value) emit('close');
      closeAfterSend.value = false;
    }
  }
};

const handleClose = () => {
  if (sendingMessage.value) {
    stopRequested.value = true;
    closeAfterSend.value = true;
    sendAbortController?.abort();
    return;
  }
  emit('close');
};

onMounted(() => {
  isMounted.value = true;
  fetchPipelines();
});

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;
    accountGeneration += 1;
    activeRequest.value += 1;
    stopRequested.value = true;
    sendAbortController?.abort();
    sendAbortController = null;
    sendingMessage.value = false;
    closeAfterSend.value = false;
    pipelines.value = [];
    selectedTemplate.value = null;
    sentTargetKeys.value = new Set();
    activeBatchFingerprint.value = '';
    activeBatchId.value = '';
    loading.value = false;
    if (isMounted.value) fetchPipelines();
  },
  { flush: 'sync' }
);

onBeforeUnmount(() => {
  isMounted.value = false;
  accountGeneration += 1;
  activeRequest.value += 1;
  stopRequested.value = true;
  sendAbortController?.abort();
  sendAbortController = null;
});
</script>

<template>
  <div class="flex max-h-[90dvh] min-h-0 flex-col">
    <header class="border-b border-n-weak px-4 py-4 pr-14 sm:px-6 sm:pr-14">
      <h2 data-modal-title class="text-xl font-semibold text-n-slate-12">
        {{ t('PIPELINE.SEND_MESSAGE.TITLE') }}
      </h2>
      <p class="mt-1 text-sm text-n-slate-11">
        {{
          isBulk
            ? t('PIPELINE.SEND_MESSAGE_TEMPLATE.BULK_DESCRIPTION', {
                count: targets.length,
              })
            : t('PIPELINE.SEND_MESSAGE_TEMPLATE.DESCRIPTION')
        }}
      </p>
    </header>

    <div class="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 sm:p-6">
      <section
        v-if="!isBulk"
        :aria-label="t('PIPELINE.SEND_MESSAGE_TEMPLATE.CONVERSATION_DETAILS')"
        class="space-y-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
      >
        <div class="flex min-w-0 items-start gap-3">
          <Avatar :src="contact?.thumbnail" :name="contact?.name" :size="40" />
          <div class="min-w-0 flex-1">
            <h3 class="truncate text-sm font-semibold text-n-slate-12">
              {{ contact?.name || t('PIPELINE.ITEM_CONVERSATION.NO_CONTACT') }}
            </h3>
            <div
              class="mt-1 flex flex-col gap-1 text-xs text-n-slate-11 sm:flex-row sm:flex-wrap sm:gap-x-4"
            >
              <span v-if="contact?.email" class="break-all">
                {{
                  t('PIPELINE.SEND_MESSAGE_TEMPLATE.CONTACT_EMAIL', {
                    value: contact.email,
                  })
                }}
              </span>
              <span v-if="contact?.phone_number">
                {{
                  t('PIPELINE.SEND_MESSAGE_TEMPLATE.CONTACT_PHONE', {
                    value: contact.phone_number,
                  })
                }}
              </span>
            </div>
          </div>
          <span
            v-if="conversation?.unread_count"
            :aria-label="
              t('PIPELINE.SEND_MESSAGE_TEMPLATE.UNREAD_COUNT', {
                count: conversation.unread_count,
              })
            "
            class="flex min-h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-n-ruby-9 px-2 text-xs font-medium text-white"
          >
            {{ conversation.unread_count }}
          </span>
        </div>

        <dl
          class="grid grid-cols-1 gap-3 border-t border-n-weak pt-3 sm:grid-cols-2"
        >
          <div>
            <dt class="text-xs font-medium text-n-slate-11">
              {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.CHANNEL_LABEL') }}
            </dt>
            <dd class="mt-1 text-sm text-n-slate-12">{{ channelName }}</dd>
          </div>
          <div>
            <dt class="text-xs font-medium text-n-slate-11">
              {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.STATUS_LABEL') }}
            </dt>
            <dd class="mt-1 text-sm text-n-slate-12">
              {{ conversationStatus }}
            </dd>
          </div>
          <div v-if="conversation?.created_at">
            <dt class="text-xs font-medium text-n-slate-11">
              {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.CREATED_AT_LABEL') }}
            </dt>
            <dd class="mt-1 text-sm text-n-slate-12">
              {{ formatDate(conversation.created_at) }}
            </dd>
          </div>
          <div v-if="conversation?.last_activity_at">
            <dt class="text-xs font-medium text-n-slate-11">
              {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.LAST_ACTIVITY_LABEL') }}
            </dt>
            <dd class="mt-1 text-sm text-n-slate-12">
              {{ formatDate(conversation.last_activity_at) }}
            </dd>
          </div>
        </dl>
      </section>

      <section class="space-y-3">
        <div
          class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
        >
          <div>
            <h3 class="text-base font-semibold text-n-slate-12">
              {{
                showCustomMessage
                  ? t('PIPELINE.SEND_MESSAGE.CUSTOM_MESSAGE')
                  : t('PIPELINE.SEND_MESSAGE_TEMPLATE.SELECT_TEMPLATE')
              }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{
                showCustomMessage
                  ? t('PIPELINE.SEND_MESSAGE_TEMPLATE.CUSTOM_MESSAGE_HINT')
                  : t('PIPELINE.SEND_MESSAGE_TEMPLATE.SELECT_TEMPLATE_HINT')
              }}
            </p>
          </div>
          <Button
            type="button"
            variant="link"
            color="blue"
            size="md"
            class="min-h-11 self-start"
            @click="toggleMessageMode"
          >
            {{
              showCustomMessage
                ? t('PIPELINE.SEND_MESSAGE.SELECT_TEMPLATE')
                : t('PIPELINE.SEND_MESSAGE.CUSTOM_MESSAGE')
            }}
          </Button>
        </div>

        <div
          v-if="loading && !showCustomMessage"
          role="status"
          aria-live="polite"
          class="flex min-h-40 items-center justify-center gap-3 text-sm text-n-slate-11"
        >
          <span
            aria-hidden="true"
            class="size-6 animate-spin rounded-full border-2 border-n-weak border-t-n-brand motion-reduce:animate-none"
          />
          {{ t('PIPELINE.MESSAGE_TEMPLATES.LOADING') }}
        </div>

        <div
          v-else-if="hasLoadError && !showCustomMessage"
          role="alert"
          class="flex min-h-40 flex-col items-center justify-center gap-3 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-5 text-center"
        >
          <p class="text-sm text-n-ruby-11">
            {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.ERROR_LOADING') }}
          </p>
          <Button
            type="button"
            variant="outline"
            color="slate"
            size="md"
            @click="fetchPipelines"
          >
            {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.RETRY') }}
          </Button>
        </div>

        <div
          v-else-if="!showCustomMessage && currentStageTemplates.length"
          role="group"
          :aria-label="t('PIPELINE.SEND_MESSAGE_TEMPLATE.SELECT_TEMPLATE')"
          class="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <button
            v-for="template in currentStageTemplates"
            :key="template.key"
            type="button"
            :aria-pressed="selectedTemplate?.key === template.key"
            class="min-h-24 rounded-xl border bg-n-solid-1 p-4 text-left outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :class="
              selectedTemplate?.key === template.key
                ? 'border-n-brand bg-n-blue-2'
                : 'border-n-weak hover:bg-n-alpha-1'
            "
            @click="selectTemplate(template)"
          >
            <span class="block truncate text-sm font-semibold text-n-slate-12">
              {{ template.title }}
            </span>
            <span
              class="mt-1 line-clamp-3 block whitespace-pre-wrap text-xs text-n-slate-11"
            >
              {{ renderTemplate(template.content, targets[0]?.item) }}
            </span>
            <span
              v-if="template.conditions?.enabled"
              class="mt-2 flex flex-wrap gap-1.5"
            >
              <span
                v-if="template.conditions?.enabled"
                class="rounded bg-n-teal-3 px-1.5 py-0.5 text-xs text-n-teal-11"
              >
                {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.BADGE_CONDITIONS') }}
              </span>
            </span>
          </button>
        </div>

        <div
          v-else-if="!showCustomMessage"
          class="flex min-h-40 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-n-weak p-5 text-center"
        >
          <fluent-icon icon="document" size="28" class="text-n-slate-9" />
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ t('PIPELINE.MESSAGE_TEMPLATES.NO_TEMPLATES') }}
          </h4>
          <p class="text-xs text-n-slate-11">
            {{ t('PIPELINE.MESSAGE_TEMPLATES.NO_TEMPLATES_DESCRIPTION') }}
          </p>
        </div>

        <div v-else class="space-y-1">
          <label
            for="pipeline-custom-message"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t('PIPELINE.SEND_MESSAGE.CUSTOM_MESSAGE') }}
          </label>
          <textarea
            id="pipeline-custom-message"
            v-model="customMessage"
            rows="6"
            :placeholder="t('PIPELINE.SEND_MESSAGE.CUSTOM_MESSAGE_PLACEHOLDER')"
            class="mb-0 min-h-36 w-full resize-y rounded-lg border-0 bg-n-alpha-black2 p-3 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak placeholder:text-n-slate-10 focus:outline-n-brand"
          />
        </div>

        <div
          v-if="selectedTemplate && !showCustomMessage"
          class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
        >
          <h4
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
          >
            {{ t('PIPELINE.SEND_MESSAGE_TEMPLATE.PREVIEW_LABEL') }}
          </h4>
          <p class="mt-2 whitespace-pre-wrap text-sm text-n-slate-12">
            {{ selectedPreview }}
          </p>
        </div>

        <p
          v-if="unresolvedVariables.length && !showCustomMessage"
          role="alert"
          class="rounded-lg bg-n-amber-3 p-3 text-sm text-n-amber-11"
        >
          {{
            t('PIPELINE.SEND_MESSAGE_TEMPLATE.UNRESOLVED_VARIABLES', {
              variables: unresolvedVariables.join(', '),
            })
          }}
        </p>
      </section>
    </div>

    <footer class="border-t border-n-weak bg-n-solid-1 p-4 sm:px-6">
      <p
        v-if="messageError"
        role="alert"
        aria-live="assertive"
        class="mb-3 rounded-lg bg-n-ruby-2 p-3 text-sm text-n-ruby-11"
      >
        {{ messageError }}
      </p>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          color="slate"
          size="md"
          class="w-full sm:w-auto"
          @click="handleClose"
        >
          {{ t('CANCEL') }}
        </Button>
        <Button
          type="button"
          variant="solid"
          color="blue"
          size="md"
          :disabled="!canSend"
          :is-loading="sendingMessage"
          class="w-full sm:w-auto"
          @click="handleSendMessage"
        >
          {{
            pendingTargets.length < targets.length
              ? t('PIPELINE.SEND_MESSAGE_TEMPLATE.RETRY_FAILED', {
                  count: pendingTargets.length,
                })
              : t('PIPELINE.SEND_MESSAGE.ACTIONS.SEND')
          }}
        </Button>
      </div>
    </footer>
  </div>
</template>
